/* eslint-disable max-lines */
/* eslint-disable indent */
import {
    useState, useContext, useEffect
} from 'react';
import {
    Button
} from 'shared/components/ui/button';
import {
    Input
} from 'shared/components/ui/input';
import {
    Switch
} from 'shared/components/ui/switch';
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'shared/components/ui/form';
import {
    Slider
} from 'shared/components/ui/slider';
import {
    Loader2, Globe
} from 'lucide-react';
import {
    useForm
} from 'react-hook-form';
import {
    zodResolver
} from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
    KBContext
} from '../knowledgeBuilderContext/KBGlobalContext';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    SubOrgContext
} from 'shared/context/SubOrganizationContext';
import {
    kbService
} from 'shared/api/services/PROJECT-O/KnowledgeBuilder.service';
import {
    useParams
} from 'react-router-dom';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from 'shared/components/ui/select';
import {
    Badge
} from 'shared/components/ui/badge';
import {
    Alert, AlertTitle, AlertDescription
} from 'shared/components/ui/alert';

// Default supported extensions to use if API call fails
const DEFAULT_SUPPORTED_EXTENSIONS = [
    'csv',
    'json',
    'xml',
    'zip',
    'gz',
    'txt',
    'pdf',
    'doc',
    'docx',
    'xls',
    'xlsx',
    'ppt',
    'pptx',
    'html',
    'htm',
    'md',
    'rtf',
];

const formSchema = z.object({
    domain_url: z.string().url({
        message: 'Please enter a valid URL'
    }),
    download_files: z.boolean().default(true),
    file_extensions: z.array(z.string()).optional(),
    all_types_of_files: z.boolean().default(false),
    max_pages: z.number().int().min(1).max(100).default(5),
    render_js: z.boolean().default(true),
    page_load_wait: z.number().min(1).max(20).default(8),
    extract_archives: z.boolean().default(true),
    max_depth: z.number().int().min(1).max(10).default(3),
    concurrent_requests: z.number().int().min(1).max(10).default(5),
    batch_size: z.number().int().min(1).max(50).default(10),
    retry_attempts: z.number().int().min(0).max(10).default(3),
    retry_wait_min: z.number().min(0.1).max(5).default(1.0),
    retry_wait_max: z.number().min(1).max(30).default(10.0),
});

export default function WebCrawlerComponent() {
    const {
        selectedOrganization
    } = useContext(OrganizationContext);
    const {
        selectedSubOrganization
    } = useContext(SubOrgContext);
    const {
        loadProcessHistory
    } = useContext(KBContext);
    const {
        collectionId
    } = useParams();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [supportedExtensions, setSupportedExtensions] = useState(
        DEFAULT_SUPPORTED_EXTENSIONS
    );
    const [selectedExtensions, setSelectedExtensions] = useState([
        'csv',
        'json',
        'xml',
        'zip',
        'gz',
        'txt',
        'pdf',
    ]);
    const [notification, setNotification] = useState(null);

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            domain_url: '',
            download_files: true,
            file_extensions: ['csv', 'json', 'xml', 'zip', 'gz', 'txt', 'pdf'],
            all_types_of_files: false,
            max_pages: 5,
            render_js: true,
            page_load_wait: 8,
            extract_archives: true,
            max_depth: 3,
            concurrent_requests: 5,
            batch_size: 10,
            retry_attempts: 3,
            retry_wait_min: 1.0,
            retry_wait_max: 10.0,
        },
    });

    useEffect(() => {
    // Fetch supported file extensions
        const fetchSupportedExtensions = async () => {
            try {
                const response = await kbService.getSupportedFileExtensions();
                if (
                    response &&
          response.data &&
          Array.isArray(response.data) &&
          response.data.length > 0
                ) {
                    setSupportedExtensions(response.data);
                } else {

                    // If the API returns no data, keep using the default extensions
                }
            } catch (error) {
                console.error('Error fetching supported extensions:', error);
                // Don't show error notification since we're falling back to defaults

            }
        };

        fetchSupportedExtensions();
    }, []);

    const handleExtensionToggle = (extension) => {
        setSelectedExtensions((prev) => {
            if (prev.includes(extension)) {
                return prev.filter((ext) => ext !== extension);
            } else {
                return [...prev, extension];
            }
        });
    };

    useEffect(() => {
        form.setValue('file_extensions', selectedExtensions);
    }, [selectedExtensions, form]);

    // Auto-dismiss notification after 5 seconds
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => {
                setNotification(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    const onSubmit = async (data) => {
        setIsSubmitting(true);

        try {
            // Ensure all required IDs are properly set
            if (!selectedOrganization?._id) {
                throw new Error('Organization ID is missing');
            }
            if (!selectedSubOrganization?._id) {
                throw new Error('Sub-organization ID is missing');
            }
            if (!collectionId) {
                throw new Error('Collection ID is missing');
            }

            const crawlData = {
                organization_id: selectedOrganization._id,
                sub_organization_id: selectedSubOrganization._id,
                collection_id: collectionId,
                ...data,
            };

            await kbService.startWebCrawl(crawlData);

            setNotification({
                type: 'success',
                title: 'Success',
                message: 'Web crawling job started successfully',
            });

            // Refresh process history to show the new crawl job
            loadProcessHistory(collectionId);
        } catch (error) {
            console.error('Error starting web crawl:', error);

            // Provide more detailed error information
            let errorMessage = 'Failed to start web crawling job';
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.error('Error response data:', error.response.data);
                console.error('Error response status:', error.response.status);

                errorMessage =
          error.response.data?.detail ||
          `Server error: ${error.response.status} - ${error.response.statusText}`;
            } else if (error.request) {
                // The request was made but no response was received
                console.error('Error request:', error.request);
                errorMessage =
          'No response received from server. Please check your network connection.';
            } else {
                // Something happened in setting up the request that triggered an Error
                errorMessage = error.message;
            }

            setNotification({
                type: 'error',
                title: 'Error',
                message: errorMessage,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex flex-col h-full max-h-[80vh] w-full">
            {/* Fixed notification area */}
            {notification && (
                <div className="flex-shrink-0 p-4 pb-0">
                    <Alert
                        variant={notification.type === 'error' ? 'destructive' : 'default'}
                        className="mb-4"
                    >
                        <AlertTitle>{notification.title}</AlertTitle>
                        <AlertDescription>{notification.message}</AlertDescription>
                    </Alert>
                </div>
            )}

            {/* Scrollable form area */}
            <div className="flex-1 overflow-auto">
                <div className="p-4">
                    <Form {...form}>
                        <form
                            onSubmit={form.handleSubmit(onSubmit)}
                            className="space-y-4 sm:space-y-6"
                        >
                            <FormField
                                control={form.control}
                                name="domain_url"
                                render={({
                                    field
                                }) => (
                                    <FormItem>
                                        <FormLabel>Domain URL</FormLabel>
                                        <FormControl>
                                            <Input placeholder="https://example.com" {...field} />
                                        </FormControl>
                                        <FormDescription>
                                            Enter the domain URL you want to crawl
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="max_pages"
                                    render={({
                                        field
                                    }) => (
                                        <FormItem>
                                            <FormLabel>Max Pages ({field.value})</FormLabel>
                                            <FormControl>
                                                <Slider
                                                    min={1}
                                                    max={100}
                                                    step={1}
                                                    defaultValue={[field.value]}
                                                    onValueChange={(value) => field.onChange(value[0])}
                                                />
                                            </FormControl>
                                            <FormDescription>
                                                Maximum number of pages to crawl
                                            </FormDescription>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="max_depth"
                                    render={({
                                        field
                                    }) => (
                                        <FormItem>
                                            <FormLabel>Max Depth ({field.value})</FormLabel>
                                            <FormControl>
                                                <Slider
                                                    min={1}
                                                    max={10}
                                                    step={1}
                                                    defaultValue={[field.value]}
                                                    onValueChange={(value) => field.onChange(value[0])}
                                                />
                                            </FormControl>
                                            <FormDescription>
                                                How deep to follow links from the starting URL
                                            </FormDescription>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="download_files"
                                    render={({
                                        field
                                    }) => (
                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                            <FormControl>
                                                <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                            <div className="space-y-1 leading-none">
                                                <FormLabel>Download Files</FormLabel>
                                                <FormDescription>
                                                    Download files from the website
                                                </FormDescription>
                                            </div>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="render_js"
                                    render={({
                                        field
                                    }) => (
                                        <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                            <FormControl>
                                                <Switch
                                                    checked={field.value}
                                                    onCheckedChange={field.onChange}
                                                />
                                            </FormControl>
                                            <div className="space-y-1 leading-none">
                                                <FormLabel>Render JavaScript</FormLabel>
                                                <FormDescription>
                                                    Wait for JavaScript to render content
                                                </FormDescription>
                                            </div>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="all_types_of_files"
                                render={({
                                    field
                                }) => (
                                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                        <FormControl>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={(checked) => {
                                                    field.onChange(checked);
                                                    if (checked) {
                                                        // If all types enabled, set all extensions
                                                        setSelectedExtensions(supportedExtensions);
                                                    }
                                                }}
                                            />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                            <FormLabel>Download All File Types</FormLabel>
                                            <FormDescription>
                                                Enable to download all supported file types
                                            </FormDescription>
                                        </div>
                                    </FormItem>
                                )}
                            />

                            {form.watch('download_files') &&
                !form.watch('all_types_of_files') && (
                <FormField
                                    control={form.control}
                                    name="file_extensions"
                                    render={() => (
                                        <FormItem>
                                            <FormLabel>File Extensions</FormLabel>
                                            <FormDescription>
                                                Select file types to download
                                            </FormDescription>
                                            <div className="mt-2">
                                                <div className="h-20 sm:h-24 w-full rounded-md border p-2 overflow-auto">
                                                    <div className="flex flex-wrap gap-2">
                                                        {supportedExtensions.map((ext) => (
                                                            <Badge
                                                                key={ext}
                                                                variant={
                                                                    selectedExtensions.includes(ext)
                                                                        ? 'default'
                                                                        : 'outline'
                                                                }
                                                                className="cursor-pointer text-xs"
                                                                onClick={() => handleExtensionToggle(ext)}
                                                            >
                                                                {ext}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="page_load_wait"
                                    render={({
                                        field
                                    }) => (
                                        <FormItem>
                                            <FormLabel>Page Load Wait ({field.value}s)</FormLabel>
                                            <FormControl>
                                                <Slider
                                                    min={1}
                                                    max={20}
                                                    step={1}
                                                    defaultValue={[field.value]}
                                                    onValueChange={(value) => field.onChange(value[0])}
                                                />
                                            </FormControl>
                                            <FormDescription>
                                                Time to wait for page to load in seconds
                                            </FormDescription>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="concurrent_requests"
                                    render={({
                                        field
                                    }) => (
                                        <FormItem>
                                            <FormLabel>
                                                Concurrent Requests ({field.value})
                                            </FormLabel>
                                            <FormControl>
                                                <Slider
                                                    min={1}
                                                    max={10}
                                                    step={1}
                                                    defaultValue={[field.value]}
                                                    onValueChange={(value) => field.onChange(value[0])}
                                                />
                                            </FormControl>
                                            <FormDescription>
                                                Number of parallel requests
                                            </FormDescription>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name="batch_size"
                                    render={({
                                        field
                                    }) => (
                                        <FormItem>
                                            <FormLabel>Batch Size</FormLabel>
                                            <FormControl>
                                                <Select
                                                    onValueChange={(value) =>
                                                        field.onChange(parseInt(value))
                                                    }
                                                    defaultValue={field.value.toString()}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select batch size" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {[5, 10, 20, 30, 50].map((size) => (
                                                            <SelectItem key={size} value={size.toString()}>
                                                                {size}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormDescription>
                                                Number of pages per processing batch
                                            </FormDescription>
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="retry_attempts"
                                    render={({
                                        field
                                    }) => (
                                        <FormItem>
                                            <FormLabel>Retry Attempts</FormLabel>
                                            <FormControl>
                                                <Select
                                                    onValueChange={(value) =>
                                                        field.onChange(parseInt(value))
                                                    }
                                                    defaultValue={field.value.toString()}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder="Select retry count" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {[0, 1, 2, 3, 5, 10].map((count) => (
                                                            <SelectItem
                                                                key={count}
                                                                value={count.toString()}
                                                            >
                                                                {count}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </FormControl>
                                            <FormDescription>
                                                Number of retry attempts for failed requests
                                            </FormDescription>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name="extract_archives"
                                render={({
                                    field
                                }) => (
                                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                        <FormControl>
                                            <Switch
                                                checked={field.value}
                                                onCheckedChange={field.onChange}
                                            />
                                        </FormControl>
                                        <div className="space-y-1 leading-none">
                                            <FormLabel>Extract Archives</FormLabel>
                                            <FormDescription>
                                                Extract contents from downloaded archive files
                                            </FormDescription>
                                        </div>
                                    </FormItem>
                                )}
                            />
                        </form>
                    </Form>
                </div>
            </div>

            {/* Fixed submit button area */}
            <div className="flex-shrink-0 p-4 pt-0 border-t bg-background">
                <div className="flex justify-end">
                    <Button
                        type="submit"
                        disabled={isSubmitting}
                        onClick={form.handleSubmit(onSubmit)}
                        className="w-full sm:w-auto"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Submitting...
                            </>
                        ) : (
                            <>
                                <Globe className="mr-2 h-4 w-4" />
                                Start Crawl
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}
